import { useEffect, useRef } from 'react';
import { CatchPoseTvOverlays } from '../games/catch-pose/index.ts';
import { FruitNinjaRemoteTvOverlays } from '../games/fruit-ninja-remote/index.ts';
import { TargetAttackTvOverlays } from '../games/target-attack/index.ts';
import { IqTvOverlays } from '../games/iq/index.ts';
import { LegKickTvOverlays } from '../games/leg-kick/index.ts';
import { RunnerTvOverlays } from '../games/runner/index.ts';
import { CalibrationAlert } from '../games/shared/index.ts';
import { startTvApp } from '../tvAppBootstrap.ts';
import { isDebugWebrtc } from '../infrastructure/WebRTCReceiver.ts';
import { LOCALES, useI18n } from 'shared/lib/i18n.tsx';

/** Диагностика `#webrtc-stats`, `#webrtc-rtt`, `#players-strip`: флаг из `renderTvPage` (NODE_ENV / TV_DEBUG_STATS) или `?debugWebrtc=1`. */
const statsFromServer = (globalThis as { __GROOVER_SHOW_WEBRTC_STATS__?: boolean }).__GROOVER_SHOW_WEBRTC_STATS__;
const showWebrtcStats = statsFromServer !== false || isDebugWebrtc();

/** Превью игр для фоновой «стены» на экране выбора игры. */
const GAME_PREVIEWS = [
  '/images/img_catch_pose_list_preview.png',
  '/images/img_target_attack_list_preview.png',
  '/images/img_leg_kick_list_preview.jpg',
  '/images/img_fruit_slice_list_preview.png',
  '/images/img_bird_flight_list_preview.jpg',
  '/images/img_runner_list_preview.jpg',
];
/**
 * Стена в шахматном порядке: нечётные колонки сдвинуты вниз на полкарточки.
 * `WALL_COLUMN_SHIFTS` — с какой игры начинается каждая колонка; подобрано так,
 * чтобы одинаковые превью не стояли рядом (ни в ряду, ни по диагонали).
 * Все колонки едут с одной скоростью — взаимное положение карточек не меняется.
 */
const WALL_COLUMN_SHIFTS = [0, 4, 1, 5, 2, 0, 3];
/** Шаг карточки по вертикали, vmin: высота 34vmin·9/16 + отступ 2.4vmin (см. `.lobby-choose-overlay__wall-col`). */
const WALL_CARD_PITCH_VMIN = 34 * (9 / 16) + 2.4;

/** Колонка стены: список со сдвигом на `shift`, повторён 4 раза — анимация уезжает на −50% и бесшовно повторяется. */
function wallColumn(shift: number): string[] {
  const shifted = GAME_PREVIEWS.map((_, i) => GAME_PREVIEWS[(i + shift) % GAME_PREVIEWS.length]);
  return [...shifted, ...shifted, ...shifted, ...shifted];
}

/**
 * Smart TV shell: стили — `tv-page.scss` → `/pages/tv-page.css`; DOM ids — как в `tvAppBootstrap`.
 */
export function TvPage() {
  const started = useRef(false);
  const stopRef = useRef<null | (() => void)>(null);
  const { locale, setLocale, t } = useI18n();

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    stopRef.current = startTvApp({ onLocaleChange: setLocale });

    return () => {
      stopRef.current?.();
      stopRef.current = null;
      started.current = false;
    };
  }, []);

  return (
    <>
      {/* Логотип скрывается в калибровке / игре (`isGameplayOnScreen` в `tvAppBootstrap`). */}
      <header className="groover-header">
        <img id="tv-logo" className="groover-header__logo" src="/images/logo-horizontal.png" alt="Groover" />
      </header>

      {showWebrtcStats && (
        <div id="players-strip" className="players-strip--empty" aria-live="polite">
          <span className="players-strip__label">{t('tv.players')}</span>
          <span id="players-strip-names">{t('tv.waiting')}</span>
        </div>
      )}

      <div id="lobby">
        <div className="lobby-layout">
          {/* Left panel */}
          <div className="lobby-left">
            <div className="lobby-lang-toggle">
              {LOCALES.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`lobby-lang-toggle__seg${locale === l ? ' lobby-lang-toggle__seg--active' : ''}`}
                  onClick={() => setLocale(l)}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="lobby-title-block">
              <h1 className="lobby-title">{t('tv.scan_qr')}</h1>
            </div>
            <p className="lobby-subtitle">{t('tv.point_camera')}</p>

            <div className="lobby-manual-code">
              <p className="lobby-manual-label">{t('tv.enter_code')}</p>
              <div className="lobby-code-tiles" id="room-code-tiles">
                <span className="lobby-code-tile">-</span>
                <span className="lobby-code-tile">-</span>
                <span className="lobby-code-tile">-</span>
                <span className="lobby-code-tile">-</span>
              </div>
            </div>

            <p className="lobby-wifi-note">
              <img src="/icons/wifi.svg" alt="" className="lobby-wifi-note__icon" width={24} height={24} />
              {t('tv.same_wifi')}
            </p>

            <a href="/help" className="lobby-help-btn">
              {t('tv.need_help')}
            </a>

            <div id="lobby-post-qr-wait" className="lobby-post-qr-wait" hidden>
              <div className="lobby-post-qr-wait__loader-wrap" aria-hidden="true">
                <div className="lobby-post-qr-wait__loader" />
              </div>
              <div className="lobby-post-qr-wait__text">
                {t('tv.post_qr_hint').split('\n').map((line, i) =>
                  line === '' ? <br key={i} /> : <span key={i}>{line}</span>,
                )}
              </div>
              <button
                type="button"
                id="lobby-post-qr-refresh-btn"
                className="lobby-post-qr-wait__refresh"
                hidden
              >
                {t('tv.refresh_page')}
              </button>
            </div>

            <div id="lobby-hint-choose" className="lobby-hint lobby-hint--choose" hidden>
              {t('tv.choose_game')}
            </div>
            <div id="lobby-hint-mode" className="lobby-hint lobby-hint--mode" hidden />
            <div id="lobby-hint-ready" className="lobby-hint lobby-hint--ready" hidden>
              <div className="lobby-hint__title" id="lobby-hint-ready-title" />
              <div className="lobby-hint__text" id="lobby-hint-ready-text" />
            </div>
          </div>

          {/* Right panel — QR */}
          <div className="lobby-right">
            <div id="qr-container">
              <img id="qr-img" src="" alt="QR Code" />
            </div>
            <button type="button" id="lobby-qr-refresh-btn" className="lobby-refresh-btn">
              <img src="/icons/autorenew.svg" alt="" width={20} height={20} />
              {t('tv.refresh_qr')}
            </button>
          </div>
        </div>
      </div>

      <div id="countdown" aria-live="assertive">
        <div className="countdown-start-fx" aria-hidden="true" />
        <img
          id="countdown-gif"
          className="countdown-gif"
          src="/media/tv-start-pulse.svg"
          alt=""
          width={200}
          height={200}
        />
        <div id="countdown-text" />
      </div>

      <canvas id="canvas" />

      <FruitNinjaRemoteTvOverlays />
      <TargetAttackTvOverlays />
      <LegKickTvOverlays />
      <CatchPoseTvOverlays />
      <RunnerTvOverlays />
      <IqTvOverlays />

      <CalibrationAlert />

      {showWebrtcStats && (
        <div id="webrtc-rtt" className="webrtc-rtt--idle" title={t('tv.p2p_rtt_title')}>
          P2P —
        </div>
      )}

      {showWebrtcStats && (
        <div
          id="webrtc-stats"
          className="webrtc-stats"
          title={t('tv.webrtc_stats_title')}
        />
      )}

      <button type="button" id="tv-send-logs-btn" className="tv-send-logs-btn" title={t('tv.send_logs')}>
        {t('tv.send_logs')}
      </button>
      <div id="tv-send-logs-loader" className="tv-send-logs-loader" hidden>
        <div className="tv-send-logs-loader__spinner" aria-hidden="true" />
        <div id="tv-send-logs-loader-text">{t('tv.sending_logs')}</div>
      </div>

      <div id="p2p-wait" className="p2p-wait" hidden role="alertdialog" aria-labelledby="p2p-wait-title">
        <div className="p2p-wait__panel">
          <div id="p2p-wait-title" className="p2p-wait__title" />
          <div id="p2p-wait-sub" className="p2p-wait__sub" />
        </div>
      </div>

      <div id="net-toast" className="net-toast" hidden role="alert" aria-live="assertive">
        <span className="net-toast__icon" aria-hidden="true" />
        <span id="net-toast-text" className="net-toast__text" />
        <button type="button" id="net-toast-dismiss" className="net-toast__btn" hidden>
          {t('tv.net.got_it')}
        </button>
      </div>

      <div id="game-unsupported-overlay" className="game-unsupported-overlay" hidden role="status">
        <div className="game-unsupported-overlay__icon-wrap">
          <img src="/icons/devices_off.svg" alt="" className="game-unsupported-overlay__icon" />
        </div>
        <h2 className="game-unsupported-overlay__title">{t('tv.game_unavailable')}</h2>
        <p className="game-unsupported-overlay__sub">
          {t('tv.game_unavailable_sub')}
        </p>
        <div className="game-unsupported-overlay__cast-icon">
          <img src="/icons/airplay.svg" alt="" width={32} height={32} />
        </div>
      </div>

      <div id="connection-lost-overlay" className="connection-lost-overlay" hidden role="alertdialog" aria-labelledby="connection-lost-title">
        <div className="connection-lost-overlay__icon-wrap">
          <img src="/icons/android_wifi_4_bar_alert.svg" alt="" className="connection-lost-overlay__icon" />
        </div>
        <h2 id="connection-lost-title" className="connection-lost-overlay__title">{t('tv.connection_lost')}</h2>
        <p className="connection-lost-overlay__sub">
          {t('tv.connection_lost_sub').split('\n').map((line, i) =>
            i === 0 ? <span key={i}>{line}<br /></span> : <span key={i}>{line}</span>
          )}
        </p>
        <button type="button" id="connection-lost-reconnect-btn" className="connection-lost-overlay__btn">
          {t('tv.reconnect')}
        </button>
      </div>

      <div id="lobby-choose-overlay" className="lobby-choose-overlay" hidden role="status">
        <div className="lobby-choose-overlay__wall" aria-hidden="true">
          {WALL_COLUMN_SHIFTS.map((shift, col) => (
            <div
              key={col}
              className="lobby-choose-overlay__wall-col"
              style={{ marginTop: `${col % 2 ? -WALL_CARD_PITCH_VMIN / 2 : 0}vmin` }}
            >
              {wallColumn(shift).map((src, i) => (
                <img key={i} src={src} alt="" />
              ))}
            </div>
          ))}
        </div>
        <div className="lobby-choose-overlay__shade" aria-hidden="true" />
        <div className="lobby-choose-overlay__icon-wrap">
          <img id="lobby-choose-overlay-icon" src="/icons/Phone.svg" alt="" className="lobby-choose-overlay__icon" />
        </div>
        <h2 id="lobby-choose-overlay-title" className="lobby-choose-overlay__title" />
        <p id="lobby-choose-overlay-sub" className="lobby-choose-overlay__sub" />
      </div>

      <div id="connecting-overlay" className="connecting-overlay" hidden role="status">
        <div id="connecting-overlay-spinner" className="connecting-overlay__spinner-wrap" aria-hidden="true">
          <div className="connecting-overlay__spinner" />
        </div>
        <img
          id="connecting-overlay-check"
          className="connecting-overlay__check"
          src="/icons/check_circle.svg"
          alt=""
          hidden
        />
        <p id="connecting-overlay-title" className="connecting-overlay__title">{t('tv.connecting')}</p>
        <p id="connecting-overlay-sub" className="connecting-overlay__sub">
          {t('tv.establishing').split('\n').map((line, i) => (
            <span key={i}>{line}{i === 0 && <br />}</span>
          ))}
        </p>
        <p id="connecting-overlay-code" className="connecting-overlay__code" />
        <div className="connecting-overlay__games">
          <p className="connecting-overlay__games-label">{t('tv.available_games')}</p>
          <div className="connecting-overlay__games-list">
            <img src="/images/img_catch_pose_list_preview.png" alt="Catch Pose" />
            <img src="/images/img_target_attack_list_preview.png" alt="Target Attack" />
            <img src="/images/img_leg_kick_list_preview.jpg" alt="Leg Kick" />
            <img src="/images/img_fruit_slice_list_preview.png" alt="Fruit Slice" />
            <img src="/images/img_bird_flight_list_preview.jpg" alt="Bird Flight" />
            <img src="/images/img_runner_list_preview.jpg" alt="Runner" />
          </div>
        </div>
      </div>
    </>
  );
}
